import { adTechLog } from './log';
import { moatIsOn, moatIsReady } from '../services/moat';

const { prerequisitesSkipOnLoad } = window.adTechConfig;

let prerequisitesResolved = false;

/**
 * Should the requirements be skipped.
 *
 * @returns {boolean}
 */
export const skipPrerequisites = () => {
	return true === prerequisitesSkipOnLoad;
};

/**
 * These prerequisites must be met before the site ads start, as a function to continue checking.
 *
 * @returns {[]}
 */
export const prerequisitesDefine = () => {

	const prerequisites = [];

	// If Moat is turned on, add it for evaluation.
	if ( moatIsOn() ) {
		prerequisites.push( moatIsReady() );
	}

	return prerequisites;
}

/**
 * Used as a simple Promise to halt any functionality until all of the prerequisites
 * have been met or the time limit has been reached.
 *
 * Example:
 * conditionsFunc returns an array of functions that resolve, all must resolve as
 * being true for the conditions to be met.
 *
 * resolvePrerequisites( conditionsFunc, 5000 ).then( () => {} );
 *
 * @param {function} conditionsFunc A function that resolves an array of conditions that must all evaluate to booleans.
 * @param {number} maxTimeMS The max amount of time to check.
 * @param {number} interval The interval to check.
 * @returns {Promise<unknown>}
 */
export const prerequisitesResolve = ( conditionsFunc, maxTimeMS = 10000, interval = 50 ) => {
	return new Promise( ( resolve, reject ) => {

		// Return early if the check has been done before.
		if ( prerequisitesResolved ) {

			adTechLog( {
				type: 'info',
				message: `All requirements have been checked and met already for google dfp.`,
			} );

			resolve();
		}

		// Resolve because the prerequisites do not need to be met.
		if ( skipPrerequisites() ) {
			prerequisitesResolved = true

			adTechLog( {
				type: 'info',
				message: `All requirements are being skipped for google dfp.`,
			} );

			resolve();
		}

		let intervalID = null,
			maxTimeMSRemaining = maxTimeMS;

		// Prevent widnow.googletag.cmd until required prerequisites have been met.
		const ready = () => {
			// Clear the interval check if it was set.
			if ( intervalID ) {
				clearInterval( intervalID );
			}

			// Allow the process to continue.
			prerequisitesResolved = true;

			resolve();
		}

		/**
		 * Make sure all evaluate to true.
		 *
		 * @returns {boolean}
		 */
		const checkAllReady = () => {
			if ( prerequisitesResolved ) {
				return true;
			}

			const allReady = conditionsFunc();

			// All have passed if the array does not include false.
			if ( typeof allReady !== 'undefined' &&
				Array.isArray( allReady ) ) {

				// Nothing to asses.
				if ( 0 === allReady.length ) {
					return true;
				}

				return ! allReady.includes( false );
			}

			// Default, false.
			return false;
		}

		/**
		 * Interval function to keep checking or reach max time.
		 */
		const checkInterval = () => {
			if ( checkAllReady() ) {
				adTechLog( {
					type: 'info',
					message: `All requirements are met after "${(maxTimeMS - maxTimeMSRemaining)}ms".`,
				} );

				ready();
			} else if ( ( maxTimeMSRemaining -= interval ) <= 0) {
				adTechLog( {
					type: 'info',
					message: `All requirements have not been met and max "${maxTimeMS}ms" time exceeded.`,
				} );

				ready();
			}
		};

		if ( checkAllReady() ) {
			adTechLog( {
				type: 'debug',
				message: `All requirements have been met without interval check.`,
			} );

			ready();
		} else {
			intervalID = setInterval( checkInterval, interval );
		}
	} );
}